/* eslint-disable no-extra-boolean-cast */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
  CurrencyCodes,
  EntityCustomField,
  EntityCustomFieldType,
  MeasurementUnits,
  OrderDeliveryType,
  OrderStatus,
  PaymentProviders,
  ProductVariant,
  SegmentType,
} from "@fena/toolkit-types";
import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import { NotificationTypes, useNotification } from "../hooks/useNotification";
import {
  CalcVariantState,
  ProductVariantApiType,
  OrderProductState,
  ProductApiType,
  ProductTypeForTables,
  ProductVariantWithParentProduct,
  FullProductSegmentPricing,
  UseQueryErrorHandlerProps,
} from "./types";
import styled from "styled-components";
import { Typography, TypographyVariant } from "../components/ui-kit";
import { Clock } from "../components/icons";
import { AddedProduct } from "../modules/private/inventory-module/incoming-inventory/types";
import { ProductVariantCartState } from "../modules/private/order-generation-module/context/OrderGenerationContextProvider";

const Row = styled.div<{ overdue?: boolean }>`
  display: flex;
  gap: 5px;

  color: ${({ overdue }) => (overdue ? "#EF6355" : "#13273F")};
  p {
    color: ${({ overdue }) => (overdue ? "#EF6355" : "#13273F")};
  }
`;

export const formatCurrency = (
  value: number | string,
  minimumFractionDigits = 2,
  currency: CurrencyCodes = CurrencyCodes.POUND_STERLING
): string => {
  let currentCurrency = currency;
  if (!Object.values(CurrencyCodes).includes(currency)) {
    currentCurrency = CurrencyCodes.POUND_STERLING;
  }

  if (typeof value === "undefined" || isNaN(value as any)) {
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "currency",
      currency: currentCurrency,
    }).format(0);
  }

  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: minimumFractionDigits ?? 0,
    maximumFractionDigits: 2,
    style: "currency",
    currency: currentCurrency,
  }).format(typeof value === "number" ? value : Number(value));
};

export const formatNumber = (
  value: number | string,
  minimumFractionDigits = 2
) => {
  if (typeof value === "undefined" || isNaN(value as any)) {
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0);
  }

  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: minimumFractionDigits ?? 0,
    maximumFractionDigits: 2,
  }).format(typeof value === "number" ? value : Number(value));
};

export const isDiscountActive = (
  productVariant?: ProductVariantApiType,
  currentCurrency: CurrencyCodes = CurrencyCodes.POUND_STERLING
) => {
  const discountsToBeChecked =
    productVariant?.pricing[currentCurrency]?.discounts;
  const currentDate = new Date();

  const activeDiscount = discountsToBeChecked?.find((discount) => {
    return (
      discount.discount > 0 &&
      discount.discountDateStart &&
      discount.discountDateEnd &&
      discount.discountDateStart <= currentDate &&
      discount.discountDateEnd > currentDate
    );
  });

  return activeDiscount;
};

export const formatAmount = (amount: string) => {
  let result = ``;
  if (amount.includes(`.`)) {
    const split = amount.split(`.`);
    if (split[1].length < 2) {
      result = `${split[0]}.${split[1]}0`;
    } else if (split[1].length > 2) {
      result = `${split[0]}.${split[1].slice(0, 2)}`;
    } else {
      result = amount;
    }
  } else {
    result = `${amount}.00`;
  }
  return result;
};

// Calculation for single item
export const calculatePrice = (
  calcVariantState: CalcVariantState,
  isWholeSale = false,
  withDelivery = false,
  customerSegmentId?: string,
  stockLocationId?: string
) => {
  const { productVariant, qty, currentCurrency } = calcVariantState;
  let variantVat = 0;
  let variantDiscount = 0;
  let variantShipping = 0;
  const { pricing, deliveryInfo } = productVariant;
  const customerSegmentedPrice = pricing?.[
    currentCurrency
  ]?.segmentPricing?.find((sp: FullProductSegmentPricing) => {
    if (
      sp.segment?.type === SegmentType.CUSTOMER &&
      sp.segment?._id === customerSegmentId &&
      (!sp.segment?.locations?.length ||
        (stockLocationId &&
          !!sp.segment?.locations?.includes(stockLocationId as any)))
    ) {
      return true;
    }
    return false;
  });
  const locationSegmentedPrice = pricing?.[
    currentCurrency
  ]?.segmentPricing?.find(
    (sp) =>
      sp.segment?.type === SegmentType.LOCATION &&
      sp.segment?.locations?.[0] === stockLocationId
  );
  const activeDiscount = isDiscountActive(productVariant);

  let variantPriceExcVat = parseFloat(
    customerSegmentedPrice?.price ??
      locationSegmentedPrice?.price ??
      pricing[currentCurrency]?.price ??
      "0"
  );

  if (isWholeSale) {
    const tier = (
      customerSegmentedPrice?.wholesaleTiers ??
      locationSegmentedPrice?.wholesaleTiers ??
      pricing[currentCurrency]?.wholesaleTiers
    )?.find((tier) => qty >= tier.minQty && qty <= tier.maxQty);

    if (tier) {
      const tierPrice = parseFloat(tier.price);
      if (!Number.isNaN(tierPrice) && tierPrice > 0) {
        variantPriceExcVat = tierPrice;
      }
    } else {
      variantPriceExcVat = parseFloat(
        customerSegmentedPrice?.wholesaleTiers?.[0]?.price ??
          locationSegmentedPrice?.wholesaleTiers?.[0]?.price ??
          pricing[currentCurrency]?.wholesaleTiers?.[0]?.price ??
          "0"
      );
    }
  }

  if (pricing[currentCurrency]?.VAT && pricing[currentCurrency]?.VAT !== 0) {
    variantVat = Number(
      (
        ((pricing[currentCurrency]?.VAT ?? 100) / 100) *
        variantPriceExcVat
      ).toFixed(2)
    );
  }
  if (!!activeDiscount) {
    variantDiscount = (activeDiscount.discount / 100) * variantPriceExcVat;
  }
  if (withDelivery && deliveryInfo?.shippingCost) {
    variantShipping = parseFloat(deliveryInfo.shippingCost);
  }
  return {
    variantSubtotal: Number(
      formatAmount(
        (
          (isWholeSale
            ? variantPriceExcVat
            : Number(
                customerSegmentedPrice?.price ??
                  locationSegmentedPrice?.price ??
                  pricing[currentCurrency]?.price ??
                  0
              )) * qty
        ).toString()
      )
    ),
    variantPriceIncVat: Number(
      formatAmount(
        (
          (isWholeSale
            ? variantPriceExcVat + variantVat
            : Number(
                customerSegmentedPrice?.priceWithVAT ??
                  locationSegmentedPrice?.priceWithVAT ??
                  pricing[currentCurrency]?.priceWithVAT ??
                  0
              ) +
              variantShipping -
              variantDiscount) * qty
        ).toString()
      )
    ),
    variantPriceExcVat: Number(
      formatAmount(
        (
          (isWholeSale
            ? variantPriceExcVat
            : Number(
                customerSegmentedPrice?.price ??
                  locationSegmentedPrice?.price ??
                  pricing[currentCurrency]?.price ??
                  0
              )) * qty
        ).toString()
      )
    ),
    variantVat: Number(formatAmount((variantVat * qty).toString())),
    variantDiscount: Number(formatAmount((variantDiscount * qty).toString())),
    variantShipping: Number(formatAmount((variantShipping * qty).toString())),
  };
};

export const calculateOrderProductPrice = (
  orderProduct: OrderProductState,
  isWholesale: boolean = false,
  withDelivery: boolean = false,
  calcInvoice: boolean = false
) => {
  const {
    discountRate,
    vatRate,
    shippingAmount,
    totalAmountPerUnit,
    amount,
    refundedQty,
    qty,
    currentCurrency,
  } = orderProduct;

  const productVariant: ProductVariantApiType | undefined =
    orderProduct.productVariant;
  const pricing = productVariant?.pricing[currentCurrency];
  const deliveryInfo = productVariant?.deliveryInfo;

  const activeDiscount = isDiscountActive(productVariant);

  let variantVat = 0;
  let variantDiscount = 0;
  let variantShipping = 0;
  let totalRefundedAmount = 0;
  let totalIncVat = 0;
  let totalIncVatDiscountShipping = 0;
  let variantPriceExcVat = 0;

  if (!!amount) {
    variantPriceExcVat = parseFloat(amount);
  } else {
    variantPriceExcVat = parseFloat(!!pricing?.price ? pricing.price : "0");
  }
  // calc vat amount per item
  if (vatRate) {
    variantVat = (vatRate / 100) * variantPriceExcVat;
  } else if (!calcInvoice && pricing?.VAT && pricing?.VAT !== 0) {
    variantVat = ((pricing.VAT ?? 0) / 100) * variantPriceExcVat;
  }

  // calc discount amount per item
  if (discountRate || !!activeDiscount) {
    variantDiscount =
      ((discountRate ?? activeDiscount?.discount ?? 0) / 100) *
      variantPriceExcVat;
  }

  const roundedVatWithDiscount = Number(
    (
      variantVat -
      variantVat * ((discountRate ?? activeDiscount?.discount ?? 0) / 100)
    ).toFixed(2)
  );

  // calc shippingCost per item
  if (withDelivery && shippingAmount) {
    variantShipping = parseFloat(shippingAmount);
  } else if (withDelivery && deliveryInfo?.shippingCost) {
    variantShipping = parseFloat(deliveryInfo.shippingCost);
  }

  // calc total amount per item
  if (!calcInvoice && totalAmountPerUnit) {
    totalIncVatDiscountShipping = parseFloat(totalAmountPerUnit);
  } else {
    totalIncVatDiscountShipping =
      Number(variantPriceExcVat.toFixed(2)) +
      variantShipping -
      variantDiscount +
      roundedVatWithDiscount;

    // totalIncVatDiscountShipping =
    //   Number((variantPriceExcVat + variantVat).toFixed(2)) +
    //   variantShipping -
    //   variantDiscount;

    totalIncVat = variantPriceExcVat + variantVat;
  }

  // calc refunded amount per item
  if (refundedQty && refundedQty > 0) {
    totalRefundedAmount = totalIncVatDiscountShipping * refundedQty;
  }

  return {
    priceIncVat: Number(
      (totalIncVat * (refundedQty ? refundedQty + qty : qty)).toFixed(2)
    ),
    priceIncVatDiscountShipping: Number(
      (
        totalIncVatDiscountShipping * (refundedQty ? refundedQty + qty : qty)
      ).toFixed(2)
    ),
    refundedPriceIncVat: Number(totalRefundedAmount.toFixed(2)),
    priceExcVat: Number(
      (variantPriceExcVat * (refundedQty ? refundedQty + qty : qty)).toFixed(2)
    ),
    vatAmount: Number(
      (
        roundedVatWithDiscount * (refundedQty ? refundedQty + qty : qty)
      ).toFixed(2)
    ),
    // vatAmount: variantVat * (refundedQty ? refundedQty + qty : qty),
    discountAmount: Number(
      (variantDiscount * (refundedQty ? refundedQty + qty : qty)).toFixed(2)
    ),
    shippingAmount: Number(
      (variantShipping * (refundedQty ? refundedQty + qty : qty)).toFixed(2)
    ),
  };
};
// End calculation for single item

// Calculation for multiple items
export const calculateAmounts = (
  productVariantCartState: ProductVariantCartState[],
  isWholesale: boolean,
  discount = 0,
  shippingCost?: string,
  customFields?: EntityCustomField[],
  customerSegmentId?: string,
  stockLocationId?: string
) => {
  let totalAmountInvVatDiscountShipping = 0;
  let subtotalAmountExcVat = 0;
  let totalVatAmount = 0;
  let subtotalAmountIncVat = 0;
  let subtotal = 0;
  let variantDiscountAmount = 0;

  if (shippingCost) {
    totalAmountInvVatDiscountShipping += parseFloat(shippingCost);
  }
  if (customFields && customFields.length > 0) {
    totalAmountInvVatDiscountShipping += customFields.reduce(
      (acc, field) =>
        acc +
        (field.type === EntityCustomFieldType.AMOUNT && !!field.value
          ? typeof Number(field.value) === "number"
            ? parseFloat(field.value)
            : 0
          : 0),
      0
    );
  }

  productVariantCartState?.forEach((prodVariant) => {
    const {
      variantPriceIncVat,
      variantPriceExcVat,
      variantVat,
      variantSubtotal,
      variantDiscount,
    } = calculatePrice(
      {
        productVariant: prodVariant.productVariant,
        qty: prodVariant.qty,
        currentCurrency: prodVariant.currentCurrency,
      },
      isWholesale,
      false,
      customerSegmentId,
      stockLocationId
    );

    subtotalAmountIncVat += variantPriceIncVat;
    subtotalAmountExcVat += variantPriceExcVat;
    totalVatAmount += variantVat;
    subtotal += variantSubtotal;
    variantDiscountAmount += variantDiscount;
  });

  totalAmountInvVatDiscountShipping +=
    subtotalAmountIncVat * (discount > 0 ? 1 - discount / 100 : 1);

  const discountValue =
    discount > 0 ? (discount / 100) * subtotalAmountExcVat : 0;
  const roundedDiscountValue = parseFloat(discountValue.toString());

  const roundedVatWithDiscount =
    totalVatAmount - totalVatAmount * (discount / 100);

  const discountForWholeOrder = roundedDiscountValue + variantDiscountAmount;

  return {
    subtotalAmountIncVat: Number(subtotalAmountIncVat.toFixed(2)),
    subtotalAmountExcVat: Number(subtotalAmountExcVat.toFixed(2)),
    discountAmountIncVat: Number(discountForWholeOrder.toFixed(2)),
    vatAmount: Number(roundedVatWithDiscount.toFixed(2)),
    subtotal: Number(subtotal.toFixed(2)),
    total: Number(totalAmountInvVatDiscountShipping.toFixed(2)),
  };
};

export const calculateOrderProductsAmounts = (
  orderProducts: OrderProductState[],
  discount = 0,
  shippingCost?: string,
  customFields?: EntityCustomField[],
  isWholesale: boolean = false,
  withDelivery: boolean = false,
  calcInvoice: boolean = false
) => {
  let totalAmountInvVatDiscountShipping = 0;
  let totalRefundedAmountIncVatDiscountShipping = 0;
  let subtotalAmountExcVat = 0;
  let totalVatAmount = 0;
  let totalShippingAmount = 0;
  let subtotalAmountIncVatDiscountShipping = 0;
  let totalDiscountAmount = 0;

  if (shippingCost) {
    totalAmountInvVatDiscountShipping += parseFloat(shippingCost);
    totalShippingAmount += parseFloat(shippingCost);
  }
  if (customFields && customFields.length > 0) {
    totalAmountInvVatDiscountShipping += customFields.reduce(
      (acc, field) =>
        acc +
        (field.type === EntityCustomFieldType.AMOUNT && !!field.value
          ? typeof Number(field.value) === "number"
            ? parseFloat(field.value)
            : 0
          : 0),
      0
    );
  }

  orderProducts?.forEach((orderProd) => {
    const {
      priceIncVatDiscountShipping,
      refundedPriceIncVat,
      priceExcVat,
      vatAmount: orderProductVat,
      shippingAmount,
      discountAmount,
    } = calculateOrderProductPrice(
      orderProd,
      isWholesale,
      withDelivery,
      calcInvoice
    );

    subtotalAmountIncVatDiscountShipping += priceIncVatDiscountShipping;
    subtotalAmountExcVat += priceExcVat;
    totalRefundedAmountIncVatDiscountShipping += refundedPriceIncVat;
    totalVatAmount += orderProductVat;
    totalShippingAmount += shippingAmount;
    totalDiscountAmount += discountAmount;
  });

  totalAmountInvVatDiscountShipping +=
    subtotalAmountIncVatDiscountShipping -
    (discount > 0
      ? subtotalAmountIncVatDiscountShipping * (discount / 100)
      : 0);

  const discountValue =
    discount > 0
      ? (discount / 100) * (subtotalAmountExcVat - totalDiscountAmount)
      : 0;
  const roundedDiscountValue = parseFloat(discountValue.toString());

  const discountForWholeOrder = roundedDiscountValue + totalDiscountAmount;

  const roundedVatWithDiscount =
    totalVatAmount - totalVatAmount * (discount / 100);

  return {
    subtotalAmountIncVatDiscountShipping: Number(
      subtotalAmountIncVatDiscountShipping.toFixed(2)
    ),
    subtotalAmountExcVat: Number(subtotalAmountExcVat.toFixed(2)),
    discountAmount: Number(discountForWholeOrder.toFixed(2)),
    vatAmount: Number(roundedVatWithDiscount.toFixed(2)),
    shippingAmount: Number(totalShippingAmount.toFixed(2)),
    total: Number(totalAmountInvVatDiscountShipping.toFixed(2)),
    totalRefundAmount:
      Number(totalRefundedAmountIncVatDiscountShipping.toFixed(2)) -
      totalRefundedAmountIncVatDiscountShipping * (discount / 100),
  };
};
// End calculation for multiple items

export const calcInventoryOrderAmounts = (
  products: AddedProduct[],
  shippingCost = "0",
  customFields: EntityCustomField[] = []
) => {
  let totalShippingAmount = 0;
  if (shippingCost) {
    totalShippingAmount = parseFloat(shippingCost);
  }
  let costExcVat = 0;
  let total = 0;
  let customFieldsAmount = 0;

  customFieldsAmount = customFields.reduce(
    (acc, field) =>
      acc +
      (field.type === EntityCustomFieldType.AMOUNT && !!field.value
        ? typeof Number(field.value) === "number"
          ? Number(field.value)
          : 0
        : 0),
    0
  );

  products.forEach((pr) => {
    const prCost = pr.amount;
    const price = parseFloat(prCost ?? "0");
    const qty = pr.qty ?? 0;

    costExcVat += price * qty;
  });

  total = costExcVat + totalShippingAmount + customFieldsAmount;

  return {
    total: Number(total.toFixed(2)),
    totalShippingAmount: Number(totalShippingAmount.toFixed(2)),
    costExcVat: Number(costExcVat.toFixed(2)),
  };
};

export const checkPaymentProvider = (provider: PaymentProviders) => {
  switch (provider) {
    case PaymentProviders.CASH:
      return "Cash";
    case PaymentProviders.CARD:
      return "Card";
    case PaymentProviders.OTHER:
      return "Other";
    case PaymentProviders.FENA_OB:
    case PaymentProviders.FENA_OB_QR:
      return "Fena Open Banking";
    case PaymentProviders.FENA_STRIPE:
      return "Card";
    default:
      return "Not defined in function";
  }
};

export const checkPossibleStatuses = (
  status: OrderStatus,
  deliveryType: OrderDeliveryType,
  paymentProvider?: PaymentProviders
) => {
  switch (status) {
    case OrderStatus.DRAFT:
      return [
        "Proceed order",
        OrderStatus.AWAITING_PAYMENT,
        OrderStatus.CANCELLED,
      ];

    case OrderStatus.AWAITING_PAYMENT: {
      if (deliveryType === OrderDeliveryType.IN_PERSON) {
        // if (paymentProvider === PaymentProviders.FENA_OB) {
        //   return [OrderStatus.CANCELLED, OrderStatus.FAILED];
        // } else {
        return [
          OrderStatus.DELIVERED,
          OrderStatus.CANCELLED,
          OrderStatus.FAILED,
        ];
        // }
      } else {
        // if (paymentProvider === PaymentProviders.FENA_OB) {
        //   return [OrderStatus.CANCELLED, OrderStatus.FAILED];
        // } else {
        return [
          OrderStatus.PREPARATION,
          OrderStatus.CANCELLED,
          OrderStatus.FAILED,
        ];
      }
    }
    // }

    case OrderStatus.PREPARATION:
      return [OrderStatus.AWAITING_DELIVERY, OrderStatus.CANCELLED];

    case OrderStatus.AWAITING_DELIVERY:
      return [OrderStatus.DELIVERY_IN_PROGRESS, OrderStatus.CANCELLED];

    case OrderStatus.DELIVERY_IN_PROGRESS:
      return [OrderStatus.DELIVERED, OrderStatus.DELIVERY_FAILURE];

    case OrderStatus.DELIVERED:
      return [
        OrderStatus.RETURN_REQUESTED,
        OrderStatus.REFUND_REQUESTED,
        OrderStatus.REFUNDED,
        OrderStatus.CANCELLED,
      ];

    case OrderStatus.DELIVERY_FAILURE:
      return [OrderStatus.CANCELLED, OrderStatus.AWAITING_DELIVERY];

    case OrderStatus.CANCELLED:
      return [OrderStatus.REFUNDED];

    case OrderStatus.RETURN_REQUESTED:
      return [OrderStatus.RETURN_IN_PROGRESS];

    case OrderStatus.RETURN_IN_PROGRESS:
      return [OrderStatus.RETURNED];

    case OrderStatus.RETURNED:
      return [OrderStatus.REFUNDED, OrderStatus.REDESPATCH];

    case OrderStatus.REDESPATCH:
      return [OrderStatus.REDESPATCH_DELIVERY_IN_PROGRESS];

    case OrderStatus.REDESPATCH_DELIVERY_IN_PROGRESS:
      return [
        OrderStatus.REDESPATCH_DELIVERED,
        OrderStatus.REDESPATCH_DELIVERY_FAILURE,
      ];

    case OrderStatus.REDESPATCH_DELIVERED:
      return [
        OrderStatus.RETURN_REQUESTED,
        OrderStatus.REFUNDED,
        OrderStatus.REFUND_REQUESTED,
      ];

    case OrderStatus.REDESPATCH_DELIVERY_FAILURE:
      return [OrderStatus.REDESPATCH, OrderStatus.CANCELLED];
    case OrderStatus.REFUND_REQUESTED:
      return [OrderStatus.REFUNDED];

    case OrderStatus.FAILED:
      if (deliveryType === OrderDeliveryType.IN_PERSON)
        return [
          OrderStatus.DELIVERED,
          OrderStatus.CANCELLED,
          OrderStatus.AWAITING_PAYMENT,
        ];
      else
        return [
          OrderStatus.PREPARATION,
          OrderStatus.CANCELLED,
          OrderStatus.AWAITING_PAYMENT,
        ];
    default:
      return [];
  }
};

export const selectedIndexesToData = (
  data: any[],
  selectedIndexes: Record<string, boolean>
) => {
  return data?.filter((_, index) => {
    return selectedIndexes[index];
  });
};

export const appendNestedObjectToFormData = (
  fd: FormData,
  data: Record<string, any>,
  parentKey?: string
) => {
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof value === "object" && !Array.isArray(value)) {
        appendNestedObjectToFormData(fd, value, formKey);
      } else {
        fd.append(formKey, value);
      }
    }
  }
};

export const checkDateCreation = (inputDate: Date) => {
  const formattedInputDate = DateTime.fromISO(String(inputDate));

  const currentDate = DateTime.now();

  const diff = currentDate.diff(formattedInputDate, [
    "years",
    "months",
    "days",
    "hours",
    "minutes",
    "seconds",
  ]);
  const diffParts = diff.toObject();

  if (diff.as("seconds") < 60) {
    return "Just now";
  } else {
    let resultString = "";
    if (diffParts.years && diffParts.years > 0) {
      resultString = `${diffParts.years} year${
        diffParts.years !== 1 ? "s" : ""
      } ago`;
    } else if (diffParts.months && diffParts.months > 0) {
      resultString = `${diffParts.months} month${
        diffParts.months !== 1 ? "s" : ""
      } ago`;
    } else if (diffParts.days && diffParts.days > 0) {
      resultString = `${diffParts.days} day${
        diffParts.days !== 1 ? "s" : ""
      } ago`;
    } else if (diffParts.hours && diffParts.hours > 0) {
      resultString = `${diffParts.hours} hour${
        diffParts.hours !== 1 ? "s" : ""
      } ago`;
    } else if (diffParts.minutes && diffParts.minutes > 0) {
      resultString = `${diffParts.minutes} minute${
        diffParts.minutes !== 1 ? "s" : ""
      } ago`;
    } else {
      resultString = "Just now";
    }
    return resultString;
  }
};

export const transformSortCode = (sortCode?: string) => {
  if (!sortCode) return "";
  if (sortCode === "None") return "None";
  let str = sortCode;
  const arr = str.split("");
  const empty: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    i === 1
      ? empty.push(arr[i], "-")
      : i === 3
      ? empty.push(arr[i], "-")
      : empty.push(arr[i]);
  }
  return (str = empty.join(""));
};

export const formatDateDifference = (dueByStr: Date, isQuote?: boolean) => {
  const currentDate = DateTime.now();
  const dueBy = DateTime.fromISO(dueByStr as unknown as string);
  const diffInHours = dueBy.diff(currentDate, "hours").hours;
  const diffInDays = dueBy.diff(currentDate, "days").days;
  const absoluteDiffInDays = Math.round(Math.abs(diffInDays));

  if (absoluteDiffInDays === 0 && diffInHours <= 12) {
    return (
      <Row>
        <div>
          <Clock />
        </div>
        <Typography variant={TypographyVariant.BODY13_REGULAR}>
          {isQuote ? "Expires" : "Due"} today
        </Typography>
      </Row>
    );
  } else if (diffInDays < 0) {
    return (
      <Row overdue>
        <div>
          <Clock />
        </div>
        <Typography variant={TypographyVariant.BODY13_REGULAR}>
          {isQuote ? "Expired" : "Overdue by"} {absoluteDiffInDays} day
          {absoluteDiffInDays !== 1 ? "s" : ""} {isQuote ? "ago" : ""}
        </Typography>
      </Row>
    );
  } else if (
    absoluteDiffInDays === 1 ||
    (absoluteDiffInDays === 0 && diffInHours > 12)
  ) {
    return (
      <Row>
        <div>
          <Clock />
        </div>
        <Typography variant={TypographyVariant.BODY13_REGULAR}>
          {isQuote ? "Expires" : "Due"} tomorrow
        </Typography>
      </Row>
    );
  } else if (absoluteDiffInDays <= 14) {
    return (
      <Row>
        <div>
          <Clock />
        </div>
        <Typography variant={TypographyVariant.BODY13_REGULAR}>
          {isQuote ? "Expires" : "Due"} in {absoluteDiffInDays} day
          {absoluteDiffInDays !== 1 ? "s" : ""}
        </Typography>
      </Row>
    );
  } else {
    return dueBy.toFormat("dd LLL, yyyy");
  }
};

export const timeSinceInception = (createdAt: Date) => {
  const inceptionDateTime = DateTime.fromISO(createdAt.toString());
  const currentDateTime = DateTime.now();

  const diffInDays = Math.floor(
    currentDateTime.diff(inceptionDateTime, "days").days
  );
  const diffInHours = Math.floor(
    currentDateTime.diff(inceptionDateTime, "hours").hours
  );
  const diffInMinutes = Math.floor(
    currentDateTime.diff(inceptionDateTime, "minutes").minutes
  );

  if (diffInDays >= 1) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"}`;
  } else if (diffInHours >= 1) {
    return `${diffInHours} ${diffInHours === 1 ? "hr" : " hrs"}`;
  } else if (diffInMinutes >= 1) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? "min" : "mins"}`;
  } else {
    return "Just created";
  }
};

export const transformStringData = (string?: string) => {
  return string
    ? string[0].toUpperCase() +
        string.slice(1).toLowerCase().replaceAll("_", " ")
    : "";
};

export const handleNavigateToUrlFromBase64 = (urlBase64: string) => {
  const a = document.createElement("a");
  a.href = urlBase64;
  a.download = `label_${DateTime.now().toFormat("dd_MMMM_y-(HH-mm)")}.pdf`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const handleDownloadPdfFromBase64 = (urlBase64: string) => {
  const a = document.createElement("a");
  a.href = urlBase64;
  a.download = `label_${DateTime.now().toFormat("dd_MMMM_y-(HH-mm)")}.pdf`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const fenaUnitsToShipengineUnits = (fenaUnit: MeasurementUnits) => {
  switch (fenaUnit) {
    case MeasurementUnits.GRAMS:
      return "gram";
    case MeasurementUnits.KILOGRAMS:
      return "kilogram";
    case MeasurementUnits.POUNDS:
      return "pound";
    case MeasurementUnits.INCHES:
      return "inch";
    case MeasurementUnits.CENTIMETERS:
      return "centimeter";
    default:
      return "centimeter";
  }
};

export const shipengineUnitsToFenaUnits = (shipengineUnit: string) => {
  switch (shipengineUnit) {
    case "gram":
      return MeasurementUnits.GRAMS;
    case "kilogram":
      return MeasurementUnits.KILOGRAMS;
    case "pound":
      return MeasurementUnits.POUNDS;
    case "inch":
      return MeasurementUnits.INCHES;
    case "centimeter":
      return MeasurementUnits.CENTIMETERS;
    default:
      return undefined;
  }
};

export const makeCorrectFormOfProducts = (
  productsData?: ProductApiType[]
): ProductTypeForTables[] => {
  return (
    productsData?.map((product) => {
      const { variants, ...rest } = product;

      const sortedVariants = variants.sort((a, b) => {
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });

      if (
        sortedVariants.length === 1 &&
        sortedVariants[0].productOptionConfig?.[0]?.option?.name === "Default"
      ) {
        return {
          ...rest,
          subRows: undefined,
          defaultVariant: { ...sortedVariants[0], parentProduct: product },
        };
      }

      const subRows = sortedVariants.map((variant) => ({
        ...variant,
        parentProduct: product,
      }));

      return {
        ...rest,
        subRows,
        defaultVariant: undefined,
      };
    }) ?? []
  );
};

export const makeVariantsFromProducts = (
  productsData?: ProductApiType[]
): ProductVariantWithParentProduct[] => {
  const variants: ProductVariantWithParentProduct[] = [];

  productsData?.forEach((product) => {
    if (product.variants && product.variants.length > 0) {
      product.variants.forEach((variant) => {
        variants.push({
          ...variant,
          parentProduct: product,
        });
      });
    }
  });

  return variants;
};

export const getVariantFromTableRowData = (
  tableRowData: ProductTypeForTables
): ProductVariantWithParentProduct => {
  return (
    tableRowData.defaultVariant ??
    tableRowData.subRows?.[0] ??
    (tableRowData as unknown as ProductVariantWithParentProduct)
  );
};

export const stringToBoolean = (str?: string) => {
  return str?.trim().toLowerCase() === "true";
};

export const findCorrespondingOrderMaxQty = (
  defaultCurrency: CurrencyCodes = CurrencyCodes.POUND_STERLING,
  isSellable: boolean,
  isWholesale: boolean,
  variant: ProductVariant,
  stockLocationId?: string,
  isTrackable?: boolean,
  fromOrderGen?: boolean
) => {
  const currentStockLocation = variant.inventoryInfo?.stockLocations?.find(
    (l) => l.location?._id === stockLocationId
  );

  const lastTierMaxQty =
    variant.pricing?.[defaultCurrency]?.wholesaleTiers?.[
      (variant?.pricing?.[defaultCurrency]?.wholesaleTiers?.length ?? 0) - 1
    ]?.maxQty ?? Number.MAX_SAFE_INTEGER;
  if (!isTrackable && fromOrderGen) {
    if (
      isWholesale &&
      variant.pricing?.[defaultCurrency]?.isWholesalePricingEnabled
    ) {
      return lastTierMaxQty;
    } else {
      return variant.maxOrderQty;
    }
  } else if (isSellable) {
    if (
      isWholesale &&
      variant.pricing?.[defaultCurrency]?.isWholesalePricingEnabled
    ) {
      return lastTierMaxQty;
    } else {
      return variant.maxOrderQty;
    }
  } else {
    if (
      isWholesale &&
      variant.pricing?.[defaultCurrency]?.isWholesalePricingEnabled &&
      lastTierMaxQty &&
      currentStockLocation
    ) {
      return Math.min(currentStockLocation.qty, lastTierMaxQty);
    } else {
      return Math.min(
        currentStockLocation?.qty ?? variant.maxOrderQty,
        variant.maxOrderQty
      );
    }
  }
};

export const calculateWeights = (products: OrderProductState[]) => {
  const conversionRates: Record<string, number> = {
    [MeasurementUnits.GRAMS]: 1,
    [MeasurementUnits.KILOGRAMS]: 1000,
    [MeasurementUnits.TONES]: 1000000,
    [MeasurementUnits.POUNDS]: 453.592,
  };

  let totalProductWeight = 0;
  let totalPackageWeight = 0;
  let totalWeightInGrams = products.reduce((total, product) => {
    const productWeight = Number(product.weight || 0);
    const productWeightInGrams =
      productWeight *
      conversionRates[product.weightUnit || MeasurementUnits.GRAMS];

    const productPackageWeight = Number(product.packageWeight || 0);
    const packageWeightInGrams =
      productPackageWeight *
      conversionRates[product.packageWeightUnit ?? MeasurementUnits.GRAMS];

    const numberOfPackages = Math.ceil(
      product.qty / (product.packagingItems ?? 1)
    );

    totalProductWeight += productWeightInGrams * product.qty;
    totalPackageWeight += packageWeightInGrams * numberOfPackages;

    return total + totalProductWeight + totalPackageWeight;
  }, 0);

  return {
    totalWeight: (totalWeightInGrams /=
      conversionRates[MeasurementUnits.KILOGRAMS]).toFixed(2),

    totalProductWeight: (totalProductWeight /=
      conversionRates[MeasurementUnits.KILOGRAMS]).toFixed(2),
    totalPackageWeight: (totalPackageWeight /=
      conversionRates[MeasurementUnits.KILOGRAMS]).toFixed(2),
  };
};

export const convertTimestampToISO = (timestamp: number | string): string => {
  const timestampInSeconds =
    typeof timestamp === "number" ? timestamp : parseInt(timestamp);
  return new Date(timestampInSeconds * 1000).toISOString();
};

export const getProductPriceRange = (
  variants: ProductVariantApiType[],
  currentCurrency: CurrencyCodes,
  isWholesale: boolean
) => {
  if (variants.length === 1) {
    return formatCurrency(
      isWholesale
        ? calculatePrice(
            {
              productVariant: variants[0],
              currentCurrency,
              qty: 1,
            },
            isWholesale
          ).variantPriceExcVat
        : calculatePrice(
            {
              productVariant: variants[0],
              currentCurrency,
              qty: 1,
            },
            isWholesale
          ).variantPriceIncVat,
      2,
      currentCurrency
    );
  } else {
    const pricesRange = variants.map((variant) => {
      return isWholesale
        ? calculatePrice(
            {
              productVariant: variant,
              currentCurrency,
              qty: 1,
            },
            isWholesale
          ).variantPriceExcVat
        : calculatePrice(
            {
              productVariant: variant,
              currentCurrency,
              qty: 1,
            },
            isWholesale
          ).variantPriceIncVat;
    });

    const minPrice = Math.min(...pricesRange);
    const maxPrice = Math.max(...pricesRange);

    return minPrice === maxPrice
      ? formatCurrency(minPrice, 2, currentCurrency)
      : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
  }
};

export const getFieldFromTransactionProviderData = (
  fieldPath: string,
  providerData?: Record<string, any>
): string | undefined => {
  if (!providerData || typeof providerData !== "object") {
    return undefined;
  }

  const fieldParts = fieldPath.split(".");
  let result = providerData;

  for (const part of fieldParts) {
    if (result && Object.prototype.hasOwnProperty.call(result, part)) {
      result = result[part];
    } else {
      return undefined;
    }
  }

  return result != null ? String(result) : undefined;
};

export const useQueryErrorHandler = ({
  isError,
  errorMessage,
  handlerId,
}: UseQueryErrorHandlerProps) => {
  const { showNotification } = useNotification();
  const shownErrors = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const key = handlerId ?? "defaultHandlerId";
    if (isError && !shownErrors.current[key]) {
      const message = errorMessage || "Failed to fetch data";

      showNotification(message, NotificationTypes.WARNING);
      shownErrors.current[key] = true;
    }
  }, [isError, errorMessage, handlerId]);
};
